import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { BookOpen, Mail, Lock, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { LogoIcon } from '@/components/Logo';
import { LanguageSelector } from '@/components/LanguageSelector';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [resending, setResending] = useState(false);
  const { login, googleLogin, apiCall } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setVerificationNeeded(false);
    try {
      const u = await login(email, password);
      toast.success('Welcome back!');
      if (u.role === 'superadmin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Login failed';
      if (status === 403 && detail.includes('verify')) {
        setVerificationNeeded(true);
      }
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const API_URL = '';
      await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      toast.success('Verification email sent! Check your inbox.');
    } catch {
      toast.error('Failed to resend');
    } finally {
      setResending(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await apiCall('post', '/auth/forgot-password', { email: forgotEmail });
      toast.success('If an account exists, a reset email has been sent.');
      setShowForgot(false);
    } catch {
      toast.error('Something went wrong');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-gradient-to-br from-background via-background to-muted/30" data-testid="login-page">
      <Helmet>
        <title>Sign In — Living Geeta | Bhagavad Gita Transformation</title>
        <meta name="description" content="Sign in to your Living Geeta account. Continue your Bhagavad Gita-powered transformation journey." />
        <link rel="canonical" href="https://livinggeeta.com/login" />
      </Helmet>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
            <LogoIcon className="w-10 h-10" />
            <span className="font-['Playfair_Display'] text-2xl font-semibold">Living Geeta</span>
          </Link>
          <LanguageSelector variant="landing" />
        </div>

        <div className="verse-card p-8">
          {!showForgot ? (
            <>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-center mb-2">{t('common.welcome_back')}</h2>
              <p className="text-muted-foreground text-center text-sm mb-8">{t('common.sign_in_to_continue')}</p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('common.email')} required
                    className="w-full bg-white/50 border border-primary/10 focus:border-primary/30 rounded-xl pl-10 pr-4 py-3 text-sm outline-none transition-colors"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <input
                    type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('common.password')} required
                    className="w-full bg-white/50 border border-primary/10 focus:border-primary/30 rounded-xl pl-10 pr-10 py-3 text-sm outline-none transition-colors"
                    data-testid="login-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3.5 text-muted-foreground hover:text-foreground">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <div className="text-right">
                  <button type="button" onClick={() => setShowForgot(true)} className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                    {t('common.forgot_password')}
                  </button>
                </div>

                <button type="submit" disabled={loading}
                  className="w-full bg-primary text-primary-foreground rounded-full py-3 font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  data-testid="login-submit-btn">
                  {loading ? '...' : t('common.login')}
                </button>
              </form>

              {verificationNeeded && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-center" data-testid="verification-banner">
                  <p className="text-sm text-amber-800 mb-2">{t('common.email_not_verified')}</p>
                  <button
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline disabled:opacity-50"
                    data-testid="resend-from-login-btn"
                  >
                    <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
                    {resending ? t('common.sending') : t('common.resend_verification')}
                  </button>
                </div>
              )}

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border/50" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-muted-foreground">{t('common.or')}</span></div>
              </div>

              <button onClick={googleLogin}
                className="w-full border border-border rounded-full py-3 font-medium hover:bg-muted/50 transition-colors flex items-center justify-center gap-2 text-sm"
                data-testid="google-login-btn">
                <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {t('common.google_login')}
              </button>

              <p className="text-center text-sm text-muted-foreground mt-6">
                {t('common.dont_have_account')}{' '}
                <Link to="/signup" className="text-primary hover:underline" data-testid="signup-link">{t('common.signup')}</Link>
              </p>
            </>
          ) : (
            <>
              <h2 className="font-['Playfair_Display'] text-2xl font-medium text-center mb-2">{t('common.reset_password')}</h2>
              <p className="text-muted-foreground text-center text-sm mb-6">{t('common.enter_email_reset')}</p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <input
                  type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder={t('common.email')} required
                  className="w-full bg-white/50 border border-primary/10 focus:border-primary/30 rounded-xl px-4 py-3 text-sm outline-none"
                  data-testid="forgot-email-input"
                />
                <button type="submit" className="w-full bg-primary text-primary-foreground rounded-full py-3 font-medium" data-testid="forgot-submit-btn">
                  {t('common.send_reset_link')}
                </button>
                <button type="button" onClick={() => setShowForgot(false)} className="w-full text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-login-btn">
                  {t('common.back_to_login')}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6 italic">
          "The soul is neither born, nor does it ever die." — Bhagavad Gita 2.20
        </p>
      </div>
    </div>
  );
}
